/**
 * Calling the product's own server routes.
 *
 * The browser has one credential — the anon key, and the session token the auth
 * server gave it. Nothing here can be given more, and nothing here should want
 * to be: a route exists precisely because a job needs something the browser must
 * never hold.
 *
 * Three things this does that a bare `fetch` would not:
 *
 *  - attaches the current access token, read from the SDK each time rather than
 *    captured once, so a refreshed session is used and an expired one is not;
 *  - turns the server's `{ error: { code, message } }` into a thrown
 *    `ApiError`, so a caller cannot accidentally treat a 403 as a success;
 *  - never repeats a message the server did not intend for a person. The server
 *    already decides what is safe to say; this does not embellish it.
 */
import { supabase } from './supabaseClient';

export class ApiError extends Error {
    constructor(
        readonly status: number,
        readonly code: string,
        override readonly message: string,
        readonly retryAfter?: number,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

/** What a caller shows when the server said nothing useful, or said nothing. */
const UNREACHABLE = 'We could not reach the server. Check your connection and try again.';

interface ErrorBody {
    error?: { code?: string; message?: string; reference?: string };
}

export async function apiPost<T>(
    path: string, body: Record<string, unknown>, options: { signal?: AbortSignal } = {},
): Promise<T> {
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
        throw new ApiError(401, 'unauthorized', 'Your session has ended. Sign in and try again.');
    }

    let response: Response;
    try {
        response = await fetch(path, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(body),
            ...(options.signal ? { signal: options.signal } : {}),
        });
    } catch {
        // Somebody pressing Cancel is not a network failure.
        if (options.signal?.aborted) throw new ApiError(0, 'cancelled', 'Cancelled.');
        throw new ApiError(0, 'unreachable', UNREACHABLE);
    }

    let text: string;
    try {
        text = await response.text();
    } catch {
        if (options.signal?.aborted) throw new ApiError(0, 'cancelled', 'Cancelled.');
        throw new ApiError(0, 'unreachable', UNREACHABLE);
    }
    let parsed: unknown = null;
    try {
        parsed = text ? JSON.parse(text) : null;
    } catch {
        parsed = null;
    }

    if (!response.ok) {
        const problem = (parsed as ErrorBody | null)?.error;
        const retryAfter = Number(response.headers.get('retry-after') ?? '') || undefined;
        throw new ApiError(
            response.status,
            problem?.code ?? 'error',
            problem?.message ?? 'That did not work. Try again.',
            retryAfter,
        );
    }

    return parsed as T;
}

/** The sentence to show for a thrown error, whatever it turned out to be. */
export const apiMessage = (error: unknown): string =>
    error instanceof ApiError ? error.message : UNREACHABLE;

/**
 * A POST whose answer is a file (the Company Data Export).
 *
 * The same token and the same error handling as `apiPost`; on success the body
 * is read as a Blob — which browsers keep on disk when it is large — together
 * with the file name the server chose. A download that stops half-way rejects
 * here rather than handing back a truncated file.
 */
export async function apiDownload(
    path: string, body: Record<string, unknown>, options: { signal?: AbortSignal } = {},
): Promise<{ blob: Blob; fileName: string | null; exportId: string | null }> {
    const { data } = await supabase().auth.getSession();
    const token = data.session?.access_token;
    if (!token) {
        throw new ApiError(401, 'unauthorized', 'Your session has ended. Sign in and try again.');
    }

    let response: Response;
    try {
        response = await fetch(path, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body),
            ...(options.signal ? { signal: options.signal } : {}),
        });
    } catch {
        if (options.signal?.aborted) throw new ApiError(0, 'cancelled', 'Cancelled.');
        throw new ApiError(0, 'unreachable', UNREACHABLE);
    }

    if (!response.ok) {
        let problem: ErrorBody['error'];
        try {
            problem = ((await response.json()) as ErrorBody | null)?.error;
        } catch {
            problem = undefined;
        }
        const retryAfter = Number(response.headers.get('retry-after') ?? '') || undefined;
        throw new ApiError(response.status, problem?.code ?? 'error',
            problem?.message ?? 'That did not work. Try again.', retryAfter);
    }

    let blob: Blob;
    try {
        blob = await response.blob();
    } catch {
        if (options.signal?.aborted) throw new ApiError(0, 'cancelled', 'Cancelled.');
        throw new ApiError(0, 'incomplete', 'The download stopped before it finished. Nothing was saved; try again.');
    }
    const disposition = response.headers.get('content-disposition') ?? '';
    const fileName = /filename="([^"]+)"/.exec(disposition)?.[1] ?? null;
    return { blob, fileName, exportId: response.headers.get('x-export-id') };
}
